import {
    Box, Slide, Typography, useMediaQuery, useTheme
} from '@mui/material';
import {
    makeStyles
} from '@mui/styles';
import {
    TypographyVariants, TypographyWeights
} from '../../figmaTheme';
import CardContainer from '../diagnostic/components/cardContainer';
import Card from '../diagnostic/components/card';
import { MedFirstDiagnosticFormContext } from '../../contexts/form';
import { useContext, useEffect, useMemo, useRef } from 'react';
import SunnysideMedLogo from '../../../public/icons/sunnyside/sunnysideMed';

const STEP_CIRCLE_SIZE = 20;
const STEP_OFFSET = 3;

const useStyles = makeStyles(theme => ({
    logoContainer: {
        margin: '0 auto 10px',
        height: '36px',
        [theme.breakpoints.down('sm')]: {
            height: '32px'
        }
    },
    disclaimer: {
    },
    disclaimerIcon: {
        position: 'absolute',
        left: 'calc(50% - 26.5px)',
        transform: 'translateY(-25px)',
        zIndex: 1
    },
    disclaimerTextBox: {
        width: 'calc(100% + 48px)',
        transform: 'translateX(-24px)',
        padding: '42px 24px 24px',
        backgroundColor: '#FBF5EC',
        marginBottom: 24
    },
    disclaimerText: {
        color: theme.palette.black
    },
    scrollableContainer: {
        overflowY: 'scroll',
        overflowX: 'hidden',
        scrollbarWidth: 'none',
        height: '100%',
        width: '100vw',
        maxWidth: '692px',
        transform: 'translate(-24px, 0)',
        padding: '0 24px'
    },
    pillHeader: {
        padding: '8px 12px',
        borderRadius: '20px',
        width: 'fit-content',
        margin: '24px 0'
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    cardContainer: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        rowGap: 24,
        marginBottom: 56,
        '&::before': {
            content: '""',
            height: '100%',
            position: 'absolute',
            backgroundImage: `linear-gradient(${theme.palette.primary['900']} 60%, rgba(255,255,255,0) 0%)`,
            backgroundPosition: 'left',
            backgroundSize: '3px 12px',
            backgroundRepeat: 'repeat-y',
            width: '100%',
            left: STEP_OFFSET + (STEP_CIRCLE_SIZE / 2) - 1, // -1 for dash consideration
            zIndex: -1
        }
    },
    stepLine: {
        borderLeftWidth: 1,
        borderLeftColor: theme.palette.primary['900'],
        borderLeftStyle: 'dotted',
        height: '100%',
        position: 'absolute',
        width: '100%'
    },
    ctaButton: {
        width: '100%'
    },
    ctaText: {
        marginTop: '24px',
        textAlign: 'left',
        pointerEvents: 'auto',
        marginBottom: '100px'
    }
}));

const useStylesProductCard = makeStyles(theme => ({
    container: {
        width: 'calc(100% + 8px)',
        marginLeft: '-4px'
    },
    step: {
        paddingLeft: STEP_OFFSET,
        paddingBottom: 16,
        display: 'flex',
        columnGap: 16
    },
    stepCircle: {
        width: STEP_CIRCLE_SIZE,
        height: STEP_CIRCLE_SIZE,
        borderRadius: '50%',
        backgroundColor: theme.palette.primary['900'],
        marginLeft: '4px'
    },
    cardContainer: {
        backgroundColor: theme.palette.primary['50'],
        borderRadius: 8,
        overflow: 'hidden'
    },
    cardContent: {
        padding: '0px 17px'
    },
    cardHeader: {
        backgroundColor: theme.palette.primary['150'],
        display: 'flex',
        justifyContent: 'space-between',
        padding: '16px'
    },
    cardHeaderName: {
        width: '65%'
    },
    cardHeaderPricing: {
        rowGap: 4,
        '& div': {
            display: 'flex',
            columnGap: 10,
            justifyContent: 'flex-end'
        }
    },
    cardBody:{
        '& ul': {
            paddingLeft: 0,
            listStyleType: 'none',
            display: 'flex',
            flexDirection: 'column',
            rowGap: 16,
            '& li': {
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                columnGap: 16,
                paddingLeft: 25
            },
            '& li::before': {
                content: '""',
                backgroundColor: theme.palette.success['500'],
                width: 9,
                height: 9,
                display: 'block',
                borderRadius: '50%',
                position: 'absolute',
                left: 0,
                top: 8
            }
        }
    },
    noteBox: {
        backgroundColor: theme.palette.success['100'],
        borderRadius: 8,
        padding: 16,
        marginTop: 16,
        marginBottom: 16
    }
}));

export type MedFirstCheckoutProductCardProps = {
    step: {
        text: string,
        subtext?: string
    }
    name: string,
    price: {
        full: number,
        discounted?: number,
        symbol: string,
        suffix?: string,
        subtext?: string
    },
    features: string[],
    note?: string,
    colors?: any
}

export const MedFirstCheckoutProductCard = ({
    step,
    name,
    price,
    features,
    note,
    colors
}: MedFirstCheckoutProductCardProps): JSX.Element => {
    const theme = useTheme();
    const classes = useStylesProductCard();
    const mainPrice = price.discounted !== undefined
        ? price.discounted
        : price.full;
    const secondaryPrice = price.discounted !== undefined
        ? price.full
        : undefined;

    return (
        <Box className={classes.container}>
            <Box className={classes.step}>
                <Box className={classes.stepCircle} style={{ backgroundColor: colors?.stepCircle || theme.palette.primary['900'] }} />
                <Box>
                    <Typography
                        variant={TypographyVariants.H5}
                        fontWeight={TypographyWeights.SEMI_BOLD}
                        color={colors?.stepText || theme.palette.primary['900']}
                    >
                        {step.text}
                    </Typography>
                    {step.subtext && (
                        <Typography
                            variant={TypographyVariants.H6}
                            color={colors?.stepTextSubtext || theme.palette.primary['700']}
                        >
                            <em>
                                {step.subtext}
                            </em>
                        </Typography>
                    )}
                </Box>
            </Box>
            <Box className={classes.cardContainer}>
                <Box
                    className={`${classes.cardContent} ${classes.cardHeader}`}
                    style={{ backgroundColor: colors?.header }}
                >
                    <Box className={classes.cardHeaderName}>
                        <Typography
                            variant={TypographyVariants.H5}
                            fontWeight={TypographyWeights.SEMI_BOLD}
                            color={colors?.headerText}
                        >
                            {name}
                        </Typography>
                    </Box>
                    <Box className={classes.cardHeaderPricing}>
                        <Box>
                            {secondaryPrice && secondaryPrice !== mainPrice && (
                                <Typography
                                    variant={TypographyVariants.H3}
                                    fontWeight={TypographyWeights.SEMI_BOLD}
                                    color={colors?.headerTextDisabled || theme.palette.primary['500']}
                                >
                                    <s>
                                        {price.symbol}
                                        {secondaryPrice.toLocaleString()}
                                    </s>
                                </Typography>
                            )}
                            <Typography
                                variant={TypographyVariants.H3}
                                color={colors?.headerText}
                            >
                                <b>
                                    {price.symbol}
                                    {mainPrice.toLocaleString()}
                                </b>
                                {price.suffix && (
                                    <Typography
                                        component='span'
                                        variant={TypographyVariants.H6}
                                        color={colors?.headerText}
                                    >
                                        {price.suffix}
                                    </Typography>
                                )}
                            </Typography>
                        </Box>
                        {price.subtext && (
                            <Box>
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    color={colors?.headerText}
                                >
                                    {price.subtext}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
                <Box className={`${classes.cardContent} ${classes.cardBody}`}>
                    <ul
                        style={{
                            margin: '16px 0'
                        }}
                    >
                        {features.map((f,i ) => (
                            <li key={i}>
                                <Typography
                                    variant={TypographyVariants.H6}
                                    color={colors?.bodyText}
                                >
                                    {f}
                                </Typography>
                            </li>
                        ))}
                    </ul>
                    {note && (
                        <Box className={classes.noteBox}>
                            <Typography
                                variant={TypographyVariants.SMALL}
                                color={theme.palette.neutral['400']}
                                textAlign='center'
                            >
                                {note}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export type MedFirstCheckoutProps = {
    onClick: () => void,
    isLoading: boolean,
    cards: MedFirstCheckoutProductCardProps[],
    sectionKey: string,
    colors?: any,
    ctaButtonText?: string
}

type PricingCardProps = {
    cards: MedFirstCheckoutProductCardProps[],
    colors?: any
}

export const PricingCards = ({
    cards,
    colors
}: PricingCardProps) => {
    const classes = useStyles();
    const anchor = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (anchor?.current) {
            anchor.current.scrollIntoView({
                behavior: 'smooth'
            });
        }
    }, [anchor?.current]);

    return (
        <>
            <Box className={classes.cardContainer}>
                {cards.map((c, i) => (
                    <MedFirstCheckoutProductCard
                        key={i}
                        colors={colors}
                        {...c}
                    />
                ))}
            </Box>
        </>
    );
};

export const DisclaimerFooter = ({
    colors,
    hideTOSDisclaimer = false
}: {
    colors: any,
    hideTOSDisclaimer?: boolean
}) => {
    const classes = useStyles();
    const theme = useTheme();
    return (
        <Box className={classes.disclaimer}>
            <Box className={classes.disclaimerIcon}>
                <svg width="53" height="53" viewBox="0 0 53 53" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="26.5" cy="26.5" r="26.5" fill={colors?.header || theme.palette.med.green} />
                    <g clipPath="url(#clip0_3049_3994)">
                        <path d="M11.408 25.6728C11.1978 25.2691 10.7001 25.1118 10.2961 25.3221C10.1815 25.3818 10.0634 25.4414 9.94205 25.5014C9.53395 25.7032 9.36653 26.1975 9.56817 26.6056C9.67773 26.8271 9.87303 26.9776 10.0942 27.0369C10.2808 27.0869 10.4857 27.0718 10.6725 26.9796C10.8045 26.9144 10.9328 26.8494 11.0573 26.7846C11.4612 26.5744 11.6182 26.0766 11.408 25.6728Z" fill="white"/>
                        <path d="M12.0902 25.2603C12.2029 25.3939 12.3489 25.4827 12.506 25.5248C12.7578 25.5923 13.0375 25.5394 13.2518 25.3586C15.0677 23.8259 15.7639 22.0168 16.9361 18.6868C17.0874 18.2574 16.8619 17.7867 16.4322 17.6355C16.0015 17.4839 15.5321 17.71 15.3809 18.1394C14.2767 21.2761 13.6799 22.8398 12.1885 24.0987C11.8403 24.3922 11.7964 24.9124 12.0902 25.2603Z" fill="white"/>
                        <path d="M38.7782 23.0031C38.7577 23.3987 39.0194 23.7435 39.3872 23.8421C39.4421 23.8568 39.4995 23.866 39.5587 23.8691C40.0133 23.8926 40.4012 23.5432 40.4246 23.0886C40.4331 22.9257 40.4384 22.7609 40.4405 22.5986C40.4463 22.1434 40.0819 21.7695 39.6268 21.7637C39.1716 21.7579 38.7978 22.1222 38.792 22.5774C38.7901 22.7184 38.7855 22.8616 38.7782 23.0031Z" fill="white"/>
                        <path d="M41.2375 33.873C39.426 31.3099 39.4201 29.284 40.1054 25.4272C40.185 24.9789 39.8859 24.5506 39.438 24.4714C38.9897 24.3918 38.5618 24.6906 38.4822 25.1388C37.7421 29.3035 37.764 31.8151 39.8911 34.8246C40.0068 34.9882 40.171 35.0969 40.3505 35.145C40.5789 35.2062 40.8319 35.1692 41.0401 35.022C41.4119 34.7592 41.5002 34.2448 41.2375 33.873Z" fill="white"/>
                        <path d="M38.7012 36.2961C38.6963 36.272 38.6904 36.2484 38.6832 36.225C38.5827 35.8299 38.4075 35.5019 38.1579 35.2524C36.3412 33.4365 35.9449 29.7136 36.9798 24.1873C37.7474 20.0884 35.7657 16.0294 32.3056 14.0388C32.701 13.6796 33.0016 13.2075 33.1498 12.6544C33.5618 11.1172 32.6462 9.53134 31.1088 9.1194C29.5716 8.70751 27.9858 9.62308 27.5739 11.1604C27.4257 11.7135 27.4502 12.2726 27.6128 12.7814C23.621 12.7752 19.8753 15.2997 18.4905 19.2332C16.6235 24.5365 14.4189 27.5624 11.9378 28.2269C11.2192 28.4193 10.6104 29.0735 10.1773 30.1193C9.8963 30.7982 9.47192 32.2679 9.87851 32.9715C9.98783 33.1608 10.1679 33.2989 10.3789 33.3554L18.8758 35.6321C18.6411 38.032 20.1704 40.3261 22.5746 40.9703C24.9788 41.6145 27.4502 40.3924 28.447 38.1967L36.9438 40.4734C37.1548 40.53 37.3798 40.5004 37.569 40.3912C38.2732 39.9848 38.6405 38.4993 38.7364 37.7706C38.8096 37.2131 38.7969 36.7179 38.7012 36.2961ZM30.6821 10.7119C31.3413 10.8886 31.7339 11.5686 31.5573 12.2278C31.3807 12.887 30.7008 13.2796 30.0414 13.1029C29.3824 12.9263 28.9898 12.2463 29.1664 11.5871C29.3431 10.9279 30.0231 10.5353 30.6821 10.7119ZM23.0011 39.3777C21.4784 38.9697 20.4779 37.5766 20.5004 36.0674L26.8219 37.7613C26.0869 39.0795 24.5239 39.7857 23.0011 39.3777ZM36.7961 38.727L11.3798 31.9167C11.4165 31.6476 11.5002 31.2656 11.6595 30.8527C11.9264 30.1616 12.2271 29.8562 12.3641 29.8194C15.4454 28.9943 17.9581 25.7106 20.0456 19.7806C21.4253 15.861 25.6287 13.6274 29.6147 14.6954C33.6007 15.7635 36.124 19.7996 35.3591 23.8838C34.8024 26.8566 34.6382 29.3467 34.8679 31.3831L31.8744 30.581C31.4348 30.4632 30.9826 30.7241 30.8647 31.1639C30.7469 31.6037 31.008 32.0558 31.4477 32.1736L35.2043 33.1802C35.3993 33.8924 35.6603 34.5305 35.9867 35.0967L28.0466 32.9691C27.607 32.8513 27.1548 33.1122 27.0369 33.552C26.9191 33.9918 27.1802 34.4439 27.6198 34.5617L37.1343 37.1111C37.1328 37.2697 37.1187 37.454 37.0856 37.6656C37.0174 38.1028 36.8988 38.4754 36.7961 38.727Z" fill="white"/>
                        <path d="M25.1803 32.2017L24.7025 32.0737C24.2628 31.9559 23.8107 32.2168 23.6929 32.6566C23.575 33.0964 23.836 33.5485 24.2758 33.6663L24.7536 33.7943C25.1932 33.9121 25.6454 33.6512 25.7632 33.2114C25.8811 32.7716 25.62 32.3195 25.1803 32.2017Z" fill="white"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_3049_3994">
                            <rect width="39.5693" height="39.5693" fill="white" transform="translate(12 4) rotate(15)"/>
                        </clipPath>
                    </defs>
                </svg>
            </Box>
            <Box
                className={classes.disclaimerTextBox}
                style={{
                    backgroundColor: colors?.disclaimerBox || '#FBF5EC'
                }}
            >
                <Typography
                    variant={TypographyVariants.H6}
                    className={classes.disclaimerText}
                >
                    {'We\'ll always remind you and let you choose if you want to refill your prescription.'}
                </Typography>
            </Box>
            {!hideTOSDisclaimer && (
                <>
                    <Typography
                        variant={TypographyVariants.SMALL}
                        className={classes.ctaText}
                        style={{
                            color: theme.palette.primary['900']
                        }}
                        dangerouslySetInnerHTML={{
                            __html: `By continuing, you agree to the <a href="https://www.sunnyside.co/terms" target="_blank" style="color:${theme.palette.primary.main};font-weight:${TypographyWeights.BOLD};text-decoration:underline;">Terms of Service</a>, and you acknowledge that, by law, shipped medication is not returnable.<br><br>Sunnyside cannot guarantee that any Sunnyside service will qualify for every FSA/HSA plan.`
                        }}
                    />
                    <Box
                        style={{
                            height: '100px'
                        }}
                    />
                </>
            )}
        </Box>
    );
};

const MedFirstCheckout = ({
    onClick,
    isLoading,
    cards,
    sectionKey,
    colors,
    ctaButtonText
}: MedFirstCheckoutProps): JSX.Element => {
    const classes = useStyles();
    const theme = useTheme();
    const anchor = useRef<HTMLDivElement>(null);
    const diagnosticFormContext = useContext(MedFirstDiagnosticFormContext);
    const section = useMemo(() => {
        return diagnosticFormContext?.sections.find(s => s.key === sectionKey);
    }, [diagnosticFormContext?.sections, sectionKey]);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    useEffect(() => {
        if (anchor?.current) {
            anchor.current.scrollIntoView({
                behavior: 'smooth'
            });
        }
    }, [anchor?.current]);
    
    return (
        <>
            <Box ref={anchor} />
            <CardContainer
                sectionKey={sectionKey}
                goBack={null}
                hideHeader={false}
                headerLogo={<></>}
                backgroundColor={theme.palette.white}
                hideArrowButton
                showCTAButton
                ctaButtonColor={theme.palette.med.yellow}
                ctaBackgroundColor={theme.palette.white}
                ctaButtonTextColor={theme.palette.black}
                isButtonLoading={isLoading}
                goNext={onClick}
                buttonText={ctaButtonText ?? 'Continue'}
            >
                <Card backgroundColor={theme.palette.white} shiftUp={true}>
                    <Box className={classes.scrollableContainer}>

                        <Slide
                            in={true}
                            direction={diagnosticFormContext.screenDirection}
                            timeout={500}
                            mountOnEnter
                            unmountOnExit
                        >
                            <Box
                                className={classes.content}
                            >
                                <Box className={classes.logoContainer}>
                                    <SunnysideMedLogo isDark />
                                </Box>
                                <Box
                                    className={classes.pillHeader}
                                    style={{
                                        backgroundColor: theme.palette.white,
                                        border: `1px solid ${theme.palette.primary['900']}`
                                    }}
                                >
                                    <Typography
                                        variant={isMobile ? TypographyVariants.H6 : TypographyVariants.H4}
                                        style={{
                                            fontWeight: TypographyWeights.SEMI_BOLD,
                                            color: theme.palette.primary['900']
                                        }}
                                    >
                                        {section?.title}
                                    </Typography>
                                </Box>
                                <PricingCards
                                    colors={colors}
                                    cards={cards}
                                />
                                <DisclaimerFooter
                                    colors={colors}
                                />
                            </Box>
                        </Slide>
                    </Box>
                </Card>
            </CardContainer>
        </>
    );
};

export default MedFirstCheckout;
